import { CPTSlab } from "./cptSlabUtils";
export interface PartQuantity {
  rootPartNumber: string;
  partNumber: string;
  quantity: number;
  partName?: string;
  partId?: string;
  mrp?: number;
}

export interface Adjustment {
  label: string;
  value: number;
}

export interface SupplierPart {
  rootPartNumber: string;
  familyQuantity: number;
  updatedAt?: number;
  supplierReportedQuantity?: number;
  source?: string;
  adjustments?: Adjustment[];
}

/** Typesense / stock-sheet parts use "Stock Sheet"; offline discovery uses this label. */
export const OFFLINE_DISCOVERY_PART_SOURCE =
  "Direct Supplier Response (Offline Discovery)";

export const isOfflineDiscoveryPart = (part: {
  source?: string;
}): boolean => part.source === OFFLINE_DISCOVERY_PART_SOURCE;

/**
 * Core pipeline helper — the ONLY place that knows how to:
 *   1. Snapshot the original supplier-reported quantity (first stage only)
 *   2. Append a new adjustment entry to the running list
 *
 * Every adjustment stage must delegate to this function instead of
 * managing those concerns itself.
 */
export const applyAdjustment = <T extends SupplierPart>(
  part: T,
  adjustment: Adjustment,
  adjustedFamilyQuantity: number,
): T => ({
  ...part,
  familyQuantity: adjustedFamilyQuantity,
  supplierReportedQuantity: part.supplierReportedQuantity ?? part.familyQuantity,
  adjustments: [...(part.adjustments ?? []), adjustment],
});

export interface SelectedSupplier {
  supplierId: string;
  supplierName: string;
  selectedParts: SupplierPart[];
  selectedAt: Date;
  distanceKm?: number;
  cptSlab?: CPTSlab;
  supplierCity?: string;
  /** True when added from the Offline Discovery tab. */
  isFromOfflineDiscovery?: boolean;
  /**
   * Live Typesense (− sold) stock by rootPartNumber for estimate inventoryItems.
   * When present, used as availableQuantity for allocated parts only.
   * Always TS − sold; never asked/confirmed part-confirmation qty.
   */
  inventoryAvailableByRootPart?: Record<string, number>;
  /**
   * When true, supplier came from part-confirmation responses and cannot be
   * removed from the Confirmed bucket.
   */
  isPartConfirmationLocked?: boolean;
}

/**
 * A single part in the payload sent to `POST /v1/part-confirmations` — mirrors the shape
 * built by `RequestPartConfirmationModal` for the single-supplier flow.
 */
export interface PartConfirmationRequestPart {
  partId: string;
  partNumber: string;
  rootPartNumber: string;
  partName?: string;
  /** Quantity we ask the supplier to confirm — min(asked, in-stock). */
  askedQty: number;
  stockQty: number;
  binLocation?: string;
  parts?: Array<Record<string, unknown>>;
  alternateParts?: Array<Record<string, unknown>>;
}

/**
 * Payload for sending part confirmation for one supplier from the estimate bucket.
 */
export interface PartConfirmationBucketEntry {
  supplierId: string;
  supplierName: string;
  addedAt: Date;
  confirmationParts: PartConfirmationRequestPart[];
  selectedParts: SupplierPart[];
}

export interface RemainingPartsCalculation {
  remainingParts: PartQuantity[];
  isFullyFulfilled: boolean;
  totalRemainingQuantity: number;
  fulfillmentPercentage: number;
}

/** Aggregated fulfilled quantity per root part (capped by original line qty). */
export const getFulfilledQuantitiesByRootPart = (
  originalParts: PartQuantity[],
  selectedSuppliers: SelectedSupplier[]
): Map<string, number> => {
  const fulfilledQuantities = new Map<string, number>();
  const originalQtyByRoot = new Map(
    originalParts.map((part) => [part.rootPartNumber, part.quantity])
  );
  selectedSuppliers.forEach((supplier) => {
    supplier.selectedParts.forEach((part) => {
      const currentFulfilled =
        fulfilledQuantities.get(part.rootPartNumber) || 0;
      const originalQuantity = originalQtyByRoot.get(part.rootPartNumber) || 0;
      const newFulfilled = Math.min(
        currentFulfilled + part.familyQuantity,
        originalQuantity
      );

      fulfilledQuantities.set(part.rootPartNumber, newFulfilled);
    });
  });
  return fulfilledQuantities;
};

export const calculateRemainingParts = (
  originalParts: PartQuantity[],
  selectedSuppliers: SelectedSupplier[]
): RemainingPartsCalculation => {
  const fulfilledQuantities = getFulfilledQuantitiesByRootPart(
    originalParts,
    selectedSuppliers
  );

  const remainingParts: PartQuantity[] = [];
  let totalRemainingQuantity = 0;

  originalParts.forEach((part) => {
    const fulfilledQuantity = fulfilledQuantities.get(part.rootPartNumber) || 0;
    const remainingQuantity = Math.max(0, part.quantity - fulfilledQuantity);

    if (remainingQuantity > 0) {
      remainingParts.push({
        ...part,
        quantity: remainingQuantity,
      });
      totalRemainingQuantity += remainingQuantity;
    }
  });

  const totalOriginalQuantity = originalParts.reduce(
    (sum, part) => sum + part.quantity,
    0
  );
  const fulfillmentPercentage =
    totalOriginalQuantity > 0
      ? Math.floor(
          ((totalOriginalQuantity - totalRemainingQuantity) /
            totalOriginalQuantity) *
            100
        )
      : 100;

  return {
    remainingParts,
    isFullyFulfilled: remainingParts.length === 0,
    totalRemainingQuantity,
    fulfillmentPercentage,
  };
};
